import React from "react";
import { useAuth } from "../../contexts/AuthContext";

/**
 * A wrapper component that conditionally renders its children
 * based on the user's permissions.
 *
 * @param {Object} props
 * @param {string|string[]} props.permission - A single permission string or an array of permission strings
 * @param {string} [props.operator='OR'] - If an array of permissions is provided, should it be 'AND' or 'OR'?
 * @param {React.ReactNode} props.children - The content to render if the user has permission
 * @param {React.ReactNode} [props.fallback=null] - Optional content to render if the user lacks permission
 */
export const HasPermission = ({
  permission,
  operator = "OR",
  children,
  fallback = null,
}) => {
  const { hasPermission } = useAuth();

  if (!permission) return children;

  const permissionsToCheck = Array.isArray(permission)
    ? permission
    : [permission];

  const hasAccess =
    operator === "AND"
      ? permissionsToCheck.every((p) => hasPermission(p))
      : permissionsToCheck.some((p) => hasPermission(p));

  return hasAccess ? children : fallback;
};

/**
 * A higher-order component that wraps a component with permission checking.
 */
export const withPermission = (Component, permission, fallback = null) => {
  return (props) => (
    <HasPermission permission={permission} fallback={fallback}>
      <Component {...props} />
    </HasPermission>
  );
};

export default HasPermission;
